import React, { useCallback, useEffect, useRef, useState } from "react";
import { supportApi } from "./supportApi";
import "./SupportCall.css";
import { startCallRingtone, stopCallRingtone } from "./callAlerts";

function requestAck(socket, event, body) {
  return new Promise((resolve, reject) => {
    socket.timeout(12000).emit(event, body, (err, reply) => {
      if (err) reject(new Error("Call connection timed out."));
      else if (!reply?.ok) reject(new Error(reply?.error || "Call failed."));
      else resolve(reply);
    });
  });
}

// Mounted once inside the global SupportAlertsProvider, not in SupportInbox.
// Thus incoming calls ring even when the admin is on another page.
export default function SupportAdminCall({
  socket,
  apiUrl,
  token,
  outgoingRequest,
}) {
  const [incoming, setIncoming] = useState(null);
  const outgoingSeq = useRef(null);
  const acceptedEarly = useRef(null);
  const onAcceptedRef = useRef(null);
  const incomingRef = useRef(null);
  const queueRef = useRef([]);
  const [stage, setStage] = useState("idle");
  const stageRef = useRef("idle");
  const [kind, setKind] = useState("audio");
  const [error, setError] = useState("");
  const [mute, setMute] = useState(false);
  const [cameraOff, setCameraOff] = useState(false);
  const [needsPlay, setNeedsPlay] = useState(false);
  const [outputSupported, setOutputSupported] = useState(false);
  const [selectedOutput, setSelectedOutput] = useState("");
  const [outputLabel, setOutputLabel] = useState("");
  const [outputError, setOutputError] = useState("");
  const outputIdRef = useRef("");
  const [remoteStream, setRemoteStream] = useState(null);
  const callIdRef = useRef("");
  const pcRef = useRef(null);
  const streamRef = useRef(null);
  const iceRef = useRef(null);
  const queuedIceRef = useRef([]);
  const localVideo = useRef(null);
  const remoteVideo = useRef(null);
  const remoteAudio = useRef(null);
  const nameRef = useRef("Customer");
  const [customerName, setCustomerName] = useState("Customer");
  const [minimized, setMinimized] = useState(false);
  const [elapsed, setElapsed] = useState(0);
  const startedAt = useRef(null);
  const recoverTimer = useRef(null);
  const restartTimer = useRef(null);
  const restartedForCall = useRef(false);

  const changeStage = (value) => {
    stageRef.current = value;
    setStage(value);
  };
  const setWaiting = (value) => {
    incomingRef.current = value;
    setIncoming(value);
  };
  const nextWaiting = () => {
    setWaiting(queueRef.current.shift() || null);
  };
  const cleanup = useCallback(() => {
    stopCallRingtone();
    if (recoverTimer.current) clearTimeout(recoverTimer.current);
    if (restartTimer.current) clearTimeout(restartTimer.current);
    recoverTimer.current = null;
    restartTimer.current = null;
    restartedForCall.current = false;
    const pc = pcRef.current;
    pcRef.current = null;
    // Avoid calling end recursively from pc.onconnectionstatechange.
    if (pc) {
      pc.onconnectionstatechange = null;
      pc.close();
    }
    streamRef.current?.getTracks().forEach((track) => track.stop());
    streamRef.current = null;
    queuedIceRef.current = [];
    iceRef.current = null;
    callIdRef.current = "";
    acceptedEarly.current = null;
    setRemoteStream(null);
    setMute(false);
    setCameraOff(false);
    setNeedsPlay(false);
    setMinimized(false);
    outputIdRef.current = "";
    setSelectedOutput("");
    setOutputLabel("");
    setOutputError("");
    startedAt.current = null;
    setElapsed(0);
    changeStage("idle");
  }, []);
  const end = useCallback(
    (reason = "") => {
      if (callIdRef.current && socket?.connected)
        socket.emit("support:call:end", { callId: callIdRef.current });
      cleanup();
      if (reason) setError(reason);
    },
    [socket, cleanup],
  );

  const makePeer = useCallback(() => {
    if (pcRef.current) return pcRef.current;
    const pc = new RTCPeerConnection(iceRef.current || { iceServers: [] });
    pcRef.current = pc;
    const remote = new MediaStream();
    pc.ontrack = (e) => {
      remote.addTrack(e.track);
      setRemoteStream(remote);
    };
    pc.onicecandidate = (e) => {
      if (e.candidate && callIdRef.current && socket?.connected) {
        socket.emit("support:call:signal", {
          callId: callIdRef.current,
          type: "ice",
          payload: e.candidate.toJSON(),
        });
      }
    };
    pc.onconnectionstatechange = () => {
      const status = pc.connectionState;
      console.info("[support admin ICE]", status, pc.iceConnectionState);
      if (status === "connected") {
        if (recoverTimer.current) clearTimeout(recoverTimer.current);
        if (restartTimer.current) clearTimeout(restartTimer.current);
        recoverTimer.current = null;
        restartTimer.current = null;
        changeStage("active");
        if (callIdRef.current && socket?.connected)
          socket.emit("support:call:media-connected", {
            callId: callIdRef.current,
          });
      } else if (
        (status === "failed" || status === "disconnected") &&
        callIdRef.current
      ) {
        changeStage("connecting");
        if (!recoverTimer.current)
          recoverTimer.current = setTimeout(() => {
            recoverTimer.current = null;
            if (pcRef.current !== pc || pc.connectionState === "connected")
              return;
            const turn = (iceRef.current?.iceServers || []).some((s) =>
              [s.urls]
                .flat()
                .flat()
                .some((u) => typeof u === "string" && /^turns?:/i.test(u)),
            );
            end(
              turn
                ? "Media failed. Verify TURN server/firewall."
                : "Mobile-to-mobile calls need TURN relay.",
            );
          }, 28000);
        // Only the agent is permitted to send a new offer by the backend.
        if (
          !restartedForCall.current &&
          !restartTimer.current &&
          socket?.connected
        ) {
          restartTimer.current = setTimeout(async () => {
            restartTimer.current = null;
            if (
              pcRef.current !== pc ||
              pc.connectionState === "connected" ||
              !callIdRef.current
            )
              return;
            if (pc.signalingState !== "stable") return; // No overlapping offers.
            restartedForCall.current = true;
            try {
              const offer = await pc.createOffer({ iceRestart: true });
              await pc.setLocalDescription(offer);
              socket.emit("support:call:signal", {
                callId: callIdRef.current,
                type: "offer",
                payload: pc.localDescription.toJSON(),
              });
            } catch (e) {
              console.warn("[support] ICE restart failed:", e);
            }
          }, 2200);
        }
      }
    };
    streamRef.current
      ?.getTracks()
      .forEach((t) => pc.addTrack(t, streamRef.current));
    return pc;
  }, [socket, end]);

  // Agent -> selected customer. The same existing default socket is used.
  const startOutgoing = useCallback(
    async (request) => {
      if (!request || stageRef.current !== "idle") return;
      if (!socket?.connected || !token) {
        setError(
          "Support connection offline. Confirm JWT and Socket.IO server connection.",
        );
        return;
      }
      setError("");
      changeStage("preparing");
      setKind(request.kind);
      setCustomerName("Customer");
      try {
        if (!window.isSecureContext || !navigator.mediaDevices?.getUserMedia)
          throw new Error("HTTPS and microphone permission are required.");
        const stream = await navigator.mediaDevices.getUserMedia({
          audio: { echoCancellation: true, noiseSuppression: true },
          video:
            request.kind === "video"
              ? { facingMode: "user", width: { ideal: 640 } }
              : false,
        });
        streamRef.current = stream;
        iceRef.current = await supportApi({
          apiUrl,
          token,
          path: "/calls/ice",
        });
        const reply = await requestAck(socket, "support:call:start", {
          conversationId: request.conversationId,
          kind: request.kind,
        });
        callIdRef.current = reply.callId;
        changeStage("ringing");
        if (acceptedEarly.current?.callId === reply.callId) {
          const early = acceptedEarly.current;
          acceptedEarly.current = null;
          await onAcceptedRef.current?.(early);
        }
      } catch (err) {
        end(err.message);
      }
    },
    [socket, apiUrl, token, end],
  );

  useEffect(() => {
    if (!outgoingRequest || outgoingSeq.current === outgoingRequest.sequence)
      return;
    outgoingSeq.current = outgoingRequest.sequence;
    void startOutgoing(outgoingRequest);
  }, [outgoingRequest, startOutgoing]);

  const answer = async () => {
    const waiting = incomingRef.current;
    if (!waiting || stageRef.current !== "idle" || !socket?.connected) return;
    changeStage("preparing");
    setError("");
    try {
      if (!window.isSecureContext || !navigator.mediaDevices?.getUserMedia)
        throw new Error("HTTPS + microphone permission required.");
      const stream = await navigator.mediaDevices.getUserMedia({
        audio: { echoCancellation: true, noiseSuppression: true },
        video:
          waiting.kind === "video"
            ? { facingMode: "user", width: { ideal: 640 } }
            : false,
      });
      streamRef.current = stream;
      iceRef.current = await supportApi({ apiUrl, token, path: "/calls/ice" });
      callIdRef.current = waiting.callId; // prepare for an early incoming offer
      const reply = await requestAck(socket, "support:call:accept", {
        callId: waiting.callId,
      });
      callIdRef.current = reply.callId;
      setKind(reply.kind);
      setCustomerName(waiting.customerName);
      nameRef.current = waiting.customerName;
      makePeer();
      stopCallRingtone();
      setWaiting(null);
      queueRef.current = [];
      changeStage("connecting");
    } catch (err) {
      if (callIdRef.current && socket?.connected) {
        socket.emit("support:call:end", { callId: callIdRef.current });
      }
      cleanup();
      setError(err.message);
      // Another agent could have picked up the call. Dismiss it on error.
      nextWaiting();
    }
  };
  const dismiss = () => {
    stopCallRingtone();
    nextWaiting();
    setError("");
  };

  useEffect(() => {
    if (!socket) return;
    const onIncoming = (payload) => {
      if (stageRef.current !== "idle") return;
      if (
        incomingRef.current?.callId === payload.callId ||
        queueRef.current.some((q) => q.callId === payload.callId)
      )
        return;
      if (incomingRef.current) queueRef.current.push(payload);
      else setWaiting(payload);
      startCallRingtone();
      setError("");
    };
    const onClaimed = ({ callId }) => {
      queueRef.current = queueRef.current.filter((q) => q.callId !== callId);
      if (incomingRef.current?.callId === callId) {
        nextWaiting();
        stopCallRingtone();
      }
    };
    const onAccepted = async ({ callId }) => {
      if (!callIdRef.current && stageRef.current === "preparing") {
        acceptedEarly.current = { callId };
        return;
      }
      if (callId !== callIdRef.current || stageRef.current !== "ringing")
        return;
      try {
        changeStage("connecting");
        const pc = makePeer();
        const offer = await pc.createOffer();
        await pc.setLocalDescription(offer);
        socket.emit("support:call:signal", {
          callId,
          type: "offer",
          payload: pc.localDescription.toJSON(),
        });
      } catch (err) {
        end(err.message);
      }
    };
    onAcceptedRef.current = onAccepted;
    const onEnded = ({ callId, reason }) => {
      if (callId === callIdRef.current) {
        cleanup();
        setError(`Call ended (${reason || "ended"}).`);
      }
      queueRef.current = queueRef.current.filter((q) => q.callId !== callId);
      if (incomingRef.current?.callId === callId) {
        nextWaiting();
        stopCallRingtone();
      }
    };
    const onSignal = async (msg) => {
      if (msg.callId !== callIdRef.current) return;
      try {
        const pc =
          pcRef.current ||
          (streamRef.current && iceRef.current ? makePeer() : null);
        if (!pc) {
          if (msg.type === "ice") queuedIceRef.current.push(msg.payload);
          return;
        }
        if (msg.type === "offer") {
          await pc.setRemoteDescription(msg.payload);
          for (const ice of queuedIceRef.current.splice(0)) {
            try {
              await pc.addIceCandidate(ice);
            } catch (e) {
              console.warn("[support] Stale ICE ignored:", e);
            }
          }
          const answerSdp = await pc.createAnswer();
          await pc.setLocalDescription(answerSdp);
          socket.emit("support:call:signal", {
            callId: msg.callId,
            type: "answer",
            payload: pc.localDescription.toJSON(),
          });
        } else if (msg.type === "answer") {
          await pc.setRemoteDescription(msg.payload);
          for (const ice of queuedIceRef.current.splice(0)) {
            try {
              await pc.addIceCandidate(ice);
            } catch (e) {
              console.warn("[support] Stale ICE ignored:", e);
            }
          }
        } else if (msg.type === "ice") {
          if (pc.remoteDescription) {
            try {
              await pc.addIceCandidate(msg.payload);
            } catch (e) {
              console.warn("[support] ICE candidate rejected:", e);
            }
          } else queuedIceRef.current.push(msg.payload);
        }
      } catch (err) {
        console.error("Support WebRTC:", err);
        end("Call negotiation failed.");
      }
    };
    const disconnect = () => {
      if (stageRef.current !== "idle") {
        cleanup();
        setError("Network disconnected.");
      }
      setWaiting(null);
      queueRef.current = [];
    };
    const syncPending = () => {
      socket.timeout(10000).emit("support:call:pending", {}, (err, result) => {
        if (!err && result?.ok) (result.calls || []).forEach(onIncoming);
      });
    };
    socket.on("connect", syncPending);
    if (socket.connected) syncPending();
    socket.on("support:call:incoming", onIncoming);
    socket.on("support:call:accepted", onAccepted);
    socket.on("support:call:claimed", onClaimed);
    socket.on("support:call:ended", onEnded);
    socket.on("support:call:signal", onSignal);
    socket.on("disconnect", disconnect);
    return () => {
      onAcceptedRef.current = null;
      socket.off("connect", syncPending);
      socket.off("support:call:incoming", onIncoming);
      socket.off("support:call:accepted", onAccepted);
      socket.off("support:call:claimed", onClaimed);
      socket.off("support:call:ended", onEnded);
      socket.off("support:call:signal", onSignal);
      socket.off("disconnect", disconnect);
    };
  }, [socket, makePeer, cleanup, end]);

  useEffect(() => {
    if (!incoming?.expiresAt) return;
    const timer = setTimeout(
      () => {
        if (incomingRef.current?.callId === incoming.callId) {
          stopCallRingtone();
          nextWaiting();
        }
      },
      Math.max(0, incoming.expiresAt - Date.now() + 250),
    );
    return () => clearTimeout(timer);
  }, [incoming]);

  useEffect(() => {
    setOutputSupported(
      typeof HTMLMediaElement !== "undefined" &&
        typeof HTMLMediaElement.prototype.setSinkId === "function",
    );
  }, []);

  const switchAudioOutput = async () => {
    const element = remoteAudio.current;
    if (!element || typeof element.setSinkId !== "function") {
      setOutputError(
        "This browser uses the phone/system audio controls for speaker routing.",
      );
      return;
    }
    try {
      setOutputError("");
      if (outputIdRef.current) {
        await element.setSinkId("");
        outputIdRef.current = "";
        setSelectedOutput("");
        setOutputLabel("");
        return;
      }
      const devices = navigator.mediaDevices;
      let target;
      if (typeof devices.selectAudioOutput === "function") {
        target = await devices.selectAudioOutput();
      } else {
        const available = (await devices.enumerateDevices()).filter(
          (d) => d.kind === "audiooutput",
        );
        target = available.find(
          (d) =>
            d.deviceId !== "default" && /speaker|loudspeaker/i.test(d.label),
        );
      }
      if (!target?.deviceId) {
        setOutputError(
          "A separate speaker device is not exposed. Use system/phone audio routing.",
        );
        return;
      }
      await element.setSinkId(target.deviceId);
      outputIdRef.current = target.deviceId;
      setSelectedOutput(target.deviceId);
      setOutputLabel(target.label || "Selected output");
      await element.play().catch(() => setNeedsPlay(true));
    } catch (e) {
      setOutputError(
        e?.name === "NotAllowedError"
          ? "Speaker selection is blocked by browser permissions."
          : `Audio output change failed: ${e.message}`,
      );
    }
  };
  useEffect(() => {
    if (localVideo.current) localVideo.current.srcObject = streamRef.current;
  }, [stage, kind, minimized]);
  useEffect(() => {
    if (!remoteStream) return;
    const audio = remoteAudio.current;
    if (audio) {
      audio.srcObject = remoteStream;
      if (outputIdRef.current && typeof audio.setSinkId === "function") {
        void audio.setSinkId(outputIdRef.current).catch(() => {
          outputIdRef.current = "";
          setSelectedOutput("");
          setOutputLabel("");
          setOutputError("Selected speaker disconnected. Using system output.");
        });
      }
      void Promise.resolve(audio.play())
        .then(() => setNeedsPlay(false))
        .catch(() => setNeedsPlay(true));
    }
    if (kind === "video" && remoteVideo.current) {
      remoteVideo.current.srcObject = remoteStream;
      void Promise.resolve(remoteVideo.current.play()).catch(() => {});
    }
  }, [stage, remoteStream, kind, minimized]);
  useEffect(() => {
    if (stage !== "active") {
      startedAt.current = null;
      setElapsed(0);
      return;
    }
    startedAt.current ??= Date.now();
    const timer = setInterval(
      () => setElapsed(Math.floor((Date.now() - startedAt.current) / 1000)),
      1000,
    );
    return () => clearInterval(timer);
  }, [stage]);
  useEffect(() => () => cleanup(), [cleanup]);

  if (!incoming && stage === "idle" && !error) return null;
  const callVisible = stage !== "idle";
  const clock = `${String(Math.floor(elapsed / 60)).padStart(2, "0")}:${String(elapsed % 60).padStart(2, "0")}`;
  const status =
    stage === "active"
      ? clock
      : stage === "preparing"
        ? "Preparing microphone…"
        : stage === "ringing"
          ? "Calling customer…"
          : stage === "connecting"
            ? "Connecting securely…"
            : "Incoming call";
  const contact = incoming?.customerName || customerName || "Customer";
  return (
    <>
      {/* One stable remote audio element for both full-screen and minimized states.
        Remote video is muted to avoid double playback/echo on video calls. */}
      <audio ref={remoteAudio} autoPlay style={{ display: "none" }} />
      {minimized && callVisible && (
        <div
          className="gp-call-mini"
          role="region"
          aria-label="Ongoing support call"
        >
          <div className="gp-mini-avatar">☎</div>
          <button
            className="gp-mini-expand-text"
            onClick={() => setMinimized(false)}
          >
            <b>{contact}</b>
            <small>{status} · Tap to expand</small>
          </button>
          <button
            className="gp-control-btn"
            aria-label="Expand call"
            title="Expand"
            onClick={() => setMinimized(false)}
          >
            ⛶
          </button>
          <button
            className="gp-control-btn gp-end-btn"
            aria-label="End call"
            onClick={() => end()}
          >
            ☎
          </button>
          <div className="gp-call-media gp-mini-media">
            {kind === "video" && (
              <div className="gp-call-video">
                <video ref={remoteVideo} autoPlay playsInline muted />
                <video ref={localVideo} autoPlay muted playsInline />
              </div>
            )}
          </div>
        </div>
      )}

      {(!minimized || !callVisible) && (
        <div
          className="gp-call-screen"
          role="dialog"
          aria-modal="true"
          aria-label="Ludobro support call"
        >
          <div className="gp-call-background" />
          <div className="gp-call-sheet">
            <header className="gp-call-header">
              <span className="gp-call-brand">☎ LUDOBRO SUPPORT</span>
              {callVisible && (
                <button
                  className="gp-control-btn"
                  onClick={() => setMinimized(true)}
                  title="Minimize without hanging up"
                  aria-label="Minimize call"
                >
                  ↙
                </button>
              )}
            </header>
            <div className="gp-call-identity">
              <div className={`gp-call-avatar ${incoming ? "gp-ringing" : ""}`}>
                👤
              </div>
              <h2>{contact}</h2>
              <p>{status}</p>
              <small>
                {kind === "video" ? "Video call" : "Voice call"} · Secure WebRTC
                connection
              </small>
            </div>

            {incoming && stage === "idle" && (
              <div className="gp-call-bottom">
                <p className="gp-call-description">
                  Customer is requesting help.
                </p>
                <div className="gp-call-buttons gp-incoming-buttons">
                  <button
                    className="gp-round-action gp-end-btn"
                    onClick={dismiss}
                  >
                    <span>✕</span>
                    <small>Decline</small>
                  </button>
                  <button
                    className="gp-round-action gp-answer-btn"
                    onClick={answer}
                  >
                    <span>☎</span>
                    <small>Accept</small>
                  </button>
                </div>
              </div>
            )}

            {callVisible && (
              <div className="gp-call-bottom">
                {kind === "video" && (
                  <div className="gp-call-video">
                    <video ref={remoteVideo} autoPlay playsInline muted />
                    <video ref={localVideo} autoPlay muted playsInline />
                  </div>
                )}

                <div className="gp-call-buttons">
                  <button
                    className="gp-round-action"
                    onClick={() => {
                      const next = !mute;
                      streamRef.current?.getAudioTracks().forEach((t) => {
                        t.enabled = !next;
                      });
                      setMute(next);
                    }}
                  >
                    <span>{mute ? "🔇" : "🎙"}</span>
                    <small>{mute ? "Unmute" : "Mute"}</small>
                  </button>
                  <button
                    type="button"
                    className="gp-round-action"
                    title={
                      outputSupported
                        ? "Choose a speaker or return to system audio"
                        : "Speaker routing is controlled by your phone/system"
                    }
                    aria-label={
                      selectedOutput
                        ? "Return to system audio"
                        : "Choose speaker output"
                    }
                    onClick={() => void switchAudioOutput()}
                  >
                    <span>{selectedOutput ? "🔈" : "🔊"}</span>
                    <small>
                      {selectedOutput
                        ? "Normal"
                        : outputSupported
                          ? "Speaker"
                          : "System audio"}
                    </small>
                  </button>
                  {kind === "video" && (
                    <button
                      className="gp-round-action"
                      onClick={() => {
                        const next = !cameraOff;
                        streamRef.current?.getVideoTracks().forEach((t) => {
                          t.enabled = !next;
                        });
                        setCameraOff(next);
                      }}
                    >
                      <span>{cameraOff ? "📵" : "📷"}</span>
                      <small>{cameraOff ? "Camera on" : "Camera off"}</small>
                    </button>
                  )}
                  <button
                    className="gp-round-action gp-end-btn"
                    onClick={() => end()}
                  >
                    <span>☎</span>
                    <small>End</small>
                  </button>
                </div>
                {(outputError || selectedOutput) && (
                  <p className="gp-output-hint" role="status">
                    {outputError ||
                      `Output: ${outputLabel}. Tap Normal for system audio.`}
                  </p>
                )}
                {needsPlay && (
                  <button
                    className="gp-call-play"
                    onClick={() => {
                      const el = remoteAudio.current;
                      Promise.resolve(el?.play())
                        .then(() => setNeedsPlay(false))
                        .catch(() => {});
                    }}
                  >
                    🔊 Tap to hear customer
                  </button>
                )}
              </div>
            )}
            {stage === "idle" && error && (
              <div className="gp-call-bottom">
                <p className="gp-call-error">{error}</p>
                <button className="gp-call-play" onClick={() => setError("")}>
                  Close
                </button>
              </div>
            )}
          </div>
        </div>
      )}
    </>
  );
}
